import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, FileText, Sparkles } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";
import conferenceAndTradeMissionService from "../../../api/ConferenceAndTradeMission";
import {
  validateGhanaPhoneNumber,
  validateSignature,
  formatGhanaPhoneNumber,
} from "../../../utils/formValidators";
import RegistrationStepper from "./RegistrationStepper";
import StepAgentReferral from "../conference_registration/StepAgentReferral"; // Reuse component
import Step1CompanyInfo from "./Step1CompanyInfo";
import Step2ContactDetails from "./Step2ContactDetails";
import Step3DescriptionServices from "./Step3DescriptionServices";
import Step4AdditionalInfo from "./Step4AdditionalInfo";
import Step5Declaration from "./Step5Declaration";
import Step6Preview from "./Step6Preview";
import generalService from "../../../api/GeneralService";

const TOTAL_STEPS = 7; // Updated to include agent referral step

export default function TradeMissionRegistration() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for referral step
  const [formData, setFormData] = useState({
    // Step 0: Agent Referral
    hasReferralCode: null,
    agentReferralCode: "",
    validatedReferralCode: false,
    agentDetails: null,
    // Step 1: Company Information
    companyName: "",
    telephoneNumber: "",
    faxNumber: "",
    websiteAddress: "",
    vatRegistrationNumber: "",
    cellNumber: "",
    businessEmail: "",
    // Step 2: Contact Details
    contactPersonName: "",
    contactPersonDesignation: "",
    contactPersonTelephone: "",
    contactPersonCellNumber: "",
    contactPersonEmail: "",
    secondContactPersonName: "",
    secondContactPersonDesignation: "",
    secondContactPersonTelephone: "",
    secondContactPersonCellNumber: "",
    secondContactPersonEmail: "",
    // Step 3: Description & Services
    companyDescription: "",
    selectedServices: [],
    otherServiceSpecification: "",
    // Step 4: Additional Info
    selectedCategories: [],
    otherCategorySpecification: "",
    // Step 5: Declaration
    applicantName: "",
    signatoryDesignation: "",
    applicantSignature: "",
    declarationDate: "",
    // Step 6: Preview
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralValidationState, setReferralValidationState] = useState({
    isValidating: false,
    isValid: false,
  });

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to register for this event");
      navigate("/signin", {
        state: { from: `/event-details/${slug}/register` },
      });
    }
  }, [isAuthenticated, navigate, slug]);

  const validateAgentReferral = async (code) => {
    try {
      // TODO: Replace with actual API endpoint when provided
      const response = await generalService.validateAgentReferralCode(code);

      if (response.data?.status_code === 200) {
        return {
          success: true,
          message:
            response.data.reason || "Referral code verified successfully!",
          agentDetails: response.data.data,
        };
      } else {
        return {
          success: false,
          message: response.data?.reason || "Invalid referral code",
        };
      }
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.reason ||
          "An error occurred while validating agent code",
      };
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Agent Referral validation
      if (formData.hasReferralCode === null) {
        newErrors.hasReferralCode =
          "Please indicate if you have a referral code";
      } else if (formData.hasReferralCode === true) {
        if (!formData.agentReferralCode?.trim()) {
          newErrors.agentReferralCode = "Please enter your referral code";
        } else if (!formData.validatedReferralCode) {
          newErrors.agentReferralCode =
            "Please verify your referral code before proceeding";
        }
      }
    }

    if (step === 1) {
      if (!formData.companyName?.trim()) {
        newErrors.companyName = "Company name is required";
      }

      const telephoneValidation = validateGhanaPhoneNumber(
        formData.telephoneNumber?.replace(/\s/g, "")
      );
      if (!formData.telephoneNumber?.trim()) {
        newErrors.telephoneNumber = "Telephone number is required";
      } else if (!telephoneValidation.isValid) {
        newErrors.telephoneNumber = telephoneValidation.message;
      }

      const cellValidation = validateGhanaPhoneNumber(
        formData.cellNumber?.replace(/\s/g, "")
      );
      if (!formData.cellNumber?.trim()) {
        newErrors.cellNumber = "Cell number is required";
      } else if (!cellValidation.isValid) {
        newErrors.cellNumber = cellValidation.message;
      }

      if (!formData.vatRegistrationNumber?.trim()) {
        newErrors.vatRegistrationNumber = "VAT registration number is required";
      }

      if (!formData.businessEmail?.trim()) {
        newErrors.businessEmail = "Business email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
        newErrors.businessEmail = "Please enter a valid email address";
      }
    }

    if (step === 2) {
      if (!formData.contactPersonName?.trim()) {
        newErrors.contactPersonName = "Contact person name is required";
      }
      if (!formData.contactPersonDesignation?.trim()) {
        newErrors.contactPersonDesignation = "Designation is required";
      }

      const contactTelValidation = validateGhanaPhoneNumber(
        formData.contactPersonTelephone?.replace(/\s/g, "")
      );
      if (!formData.contactPersonTelephone?.trim()) {
        newErrors.contactPersonTelephone = "Telephone number is required";
      } else if (!contactTelValidation.isValid) {
        newErrors.contactPersonTelephone = contactTelValidation.message;
      }

      const contactCellValidation = validateGhanaPhoneNumber(
        formData.contactPersonCellNumber?.replace(/\s/g, "")
      );
      if (!formData.contactPersonCellNumber?.trim()) {
        newErrors.contactPersonCellNumber = "Cell number is required";
      } else if (!contactCellValidation.isValid) {
        newErrors.contactPersonCellNumber = contactCellValidation.message;
      }

      if (!formData.contactPersonEmail?.trim()) {
        newErrors.contactPersonEmail = "Email address is required";
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactPersonEmail)
      ) {
        newErrors.contactPersonEmail = "Please enter a valid email address";
      }

      if (!formData.secondContactPersonName?.trim()) {
        newErrors.secondContactPersonName =
          "Second contact person name is required";
      }
      if (!formData.secondContactPersonDesignation?.trim()) {
        newErrors.secondContactPersonDesignation = "Designation is required";
      }

      const secondTelValidation = validateGhanaPhoneNumber(
        formData.secondContactPersonTelephone?.replace(/\s/g, "")
      );
      if (!formData.secondContactPersonTelephone?.trim()) {
        newErrors.secondContactPersonTelephone = "Telephone number is required";
      } else if (!secondTelValidation.isValid) {
        newErrors.secondContactPersonTelephone = secondTelValidation.message;
      }

      const secondCellValidation = validateGhanaPhoneNumber(
        formData.secondContactPersonCellNumber?.replace(/\s/g, "")
      );
      if (!formData.secondContactPersonCellNumber?.trim()) {
        newErrors.secondContactPersonCellNumber = "Cell number is required";
      } else if (!secondCellValidation.isValid) {
        newErrors.secondContactPersonCellNumber = secondCellValidation.message;
      }

      if (!formData.secondContactPersonEmail?.trim()) {
        newErrors.secondContactPersonEmail = "Email address is required";
      } else if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondContactPersonEmail)
      ) {
        newErrors.secondContactPersonEmail =
          "Please enter a valid email address";
      }
    }

    if (step === 3) {
      // Company Description
      if (!formData.companyDescription?.trim()) {
        newErrors.companyDescription = "Company description is required";
      } else if (formData.companyDescription.trim().length < 50) {
        newErrors.companyDescription =
          "Please provide a more detailed description (at least 50 characters)";
      }

      // Selected Services
      if (
        !formData.selectedServices ||
        formData.selectedServices.length === 0
      ) {
        newErrors.selectedServices =
          "Please select at least one service of interest";
      }

      // If "Other" is selected, require specification
      if (
        formData.selectedServices?.includes("other") &&
        !formData.otherServiceSpecification?.trim()
      ) {
        newErrors.otherServiceSpecification =
          "Please specify the other service";
      }
    }

    if (step === 4) {
      // Selected Categories
      if (
        !formData.selectedCategories ||
        formData.selectedCategories.length === 0
      ) {
        newErrors.selectedCategories = "Please select at least one category";
      }

      // If "Other" is selected, require specification
      if (
        formData.selectedCategories?.includes("other") &&
        !formData.otherCategorySpecification?.trim()
      ) {
        newErrors.otherCategorySpecification =
          "Please specify the other category";
      }
    }

    if (step === 5) {
      if (!formData.applicantName?.trim()) {
        newErrors.applicantName = "Applicant name is required";
      }
      if (!formData.signatoryDesignation?.trim()) {
        newErrors.signatoryDesignation = "Signatory designation is required";
      }

      const signatureValidation = validateSignature(
        formData.applicantSignature,
        formData.applicantName
      );
      if (!formData.applicantSignature?.trim()) {
        newErrors.applicantSignature = "Applicant signature is required";
      } else if (!signatureValidation.isValid) {
        newErrors.applicantSignature = signatureValidation.message;
      }

      if (!formData.declarationDate?.trim()) {
        newErrors.declarationDate = "Declaration date is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // If on preview step (last step), submit the form
      if (currentStep === TOTAL_STEPS - 1) {
        handleSubmit();
      } else if (currentStep < TOTAL_STEPS - 1) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const transformFormDataToPayload = () => {
    const clientSlug = user?.slug || user?.client_slug;
    if (!clientSlug) {
      toast.error("Your account session is missing. Please sign in again.");
      throw new Error("client_slug is required for registration");
    }
    const payload = {
      client_slug: clientSlug,
      event_type: "trade_mission",
      event_slug: slug,
      company_name: formData.companyName,
      telephone_number: formatGhanaPhoneNumber(
        formData.telephoneNumber?.replace(/\s/g, "")
      ),
      cell_number: formatGhanaPhoneNumber(
        formData.cellNumber?.replace(/\s/g, "")
      ),
      business_email: formData.businessEmail,
      vat_registration_number: formData.vatRegistrationNumber,
      contact_person_name: formData.contactPersonName,
      contact_person_designation: formData.contactPersonDesignation,
      contact_person_telephone: formatGhanaPhoneNumber(
        formData.contactPersonTelephone?.replace(/\s/g, "")
      ),
      contact_person_cell_number: formatGhanaPhoneNumber(
        formData.contactPersonCellNumber?.replace(/\s/g, "")
      ),
      contact_person_email: formData.contactPersonEmail,
      second_contact_person_name: formData.secondContactPersonName,
      second_contact_person_designation:
        formData.secondContactPersonDesignation,
      second_contact_person_telephone: formatGhanaPhoneNumber(
        formData.secondContactPersonTelephone?.replace(/\s/g, "")
      ),
      second_contact_person_cell_number: formatGhanaPhoneNumber(
        formData.secondContactPersonCellNumber?.replace(/\s/g, "")
      ),
      second_contact_person_email: formData.secondContactPersonEmail,
      company_description: formData.companyDescription,
      selected_services: formData.selectedServices,
      selected_categories: formData.selectedCategories,
      applicant_name: formData.applicantName,
      signatory_designation: formData.signatoryDesignation,
      applicant_signature: formData.applicantSignature,
      declaration_date: formData.declarationDate,
      agent_code:
        formData.hasReferralCode &&
        formData.agentReferralCode &&
        formData.validatedReferralCode
          ? formData.agentReferralCode
          : null,
    };

    if (
      formData.hasReferralCode &&
      formData.agentReferralCode &&
      formData.validatedReferralCode
    ) {
      payload.agent_referral_code = formData.agentReferralCode;
    }

    if (formData.faxNumber) {
      payload.fax_number = formData.faxNumber;
    }

    if (formData.websiteAddress) {
      payload.website_address = formData.websiteAddress;
    }

    if (formData.selectedServices?.includes("other")) {
      payload.other_service_specification = formData.otherServiceSpecification?.trim() || "";
    } else {
      payload.other_service_specification = "";
    }

    if (formData.selectedCategories?.includes("other")) {
      payload.other_category_specification = formData.otherCategorySpecification?.trim() || "";
    } else {
      payload.other_category_specification = "";
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      toast.error(
        "Please complete all required fields in the declaration step"
      );
      setCurrentStep(6);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = transformFormDataToPayload();
      const response =
        await conferenceAndTradeMissionService.registerTradeMission(payload);

      if (response.data.status_code === 200) {
        toast.success(
          response.data.reason || "Registration submitted successfully!"
        );
        navigate(`/event-details/${slug}/registration-success`, {
          state: {
            applicationId: response.data?.application_id || response.data?.id,
            registrationId: response.data?.id || response.data?.registration_id,
            registrationData: response.data,
          },
        });
      } else {
        toast.error(
          response.message || "Failed to submit registration. Please try again."
        );
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (currentStep > 0) {
      if (
        window.confirm(
          "Are you sure you want to leave? Your progress will be lost."
        )
      ) {
        navigate(`/event-details/${slug}`);
      }
    } else {
      navigate(`/event-details/${slug}`);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepAgentReferral
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            onValidateReferral={validateAgentReferral}
            onValidationStateChange={setReferralValidationState}
          />
        );
      case 1:
        return (
          <Step1CompanyInfo
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <Step2ContactDetails
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <Step3DescriptionServices
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <Step4AdditionalInfo
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 5:
        return (
          <Step5Declaration
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 6:
        return <Step6Preview formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Enhanced Header */}
      <div className="overflow-hidden sticky top-0 z-20 mt-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 shadow-lg">
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
              {/* Left: Back Button */}
              <motion.button
                onClick={handleClose}
                whileHover={{ x: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex gap-2 items-center self-start px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg transition-all hover:bg-white/30 hover:shadow-xl"
              >
                <ChevronLeft size={18} className="text-white" />
                <span>Back to Event</span>
              </motion.button>

              {/* Center: Title Section */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-2 items-center text-center sm:flex-1"
              >
                <div className="flex gap-2 items-center">
                  <div className="flex justify-center items-center w-10 h-10 text-orange-600 bg-white rounded-xl shadow-lg">
                    <FileText size={20} />
                  </div>
                  <h1 className="text-2xl font-bold text-white sm:text-3xl">
                    Membership Application
                  </h1>
                </div>
                <p className="text-sm text-orange-100 sm:text-base">
                  Complete the form below to register for this trade mission
                </p>
              </motion.div>

              {/* Right: Decorative Element */}
              <div className="hidden justify-center items-center w-12 h-12 sm:flex text-white/20">
                <Sparkles size={24} />
              </div>
            </div>

            {/* Progress Indicator Bar */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="overflow-hidden mt-6 h-1 rounded-full bg-white/20"
            >
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: currentStep / TOTAL_STEPS }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="h-full bg-white rounded-full shadow-lg"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Progress Stepper */}
          <div className="mb-8">
            <RegistrationStepper currentStep={currentStep} />
          </div>

          {/* Form Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
          </motion.div>

          {/* Footer Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="sticky bottom-0 px-8 py-6 mt-8 bg-white rounded-xl border border-gray-200 shadow-lg"
          >
            <div className="flex justify-between items-center">
              <motion.button
                onClick={handleBack}
                disabled={currentStep === 0}
                whileHover={currentStep > 0 ? { scale: 1.02 } : {}}
                whileTap={currentStep > 0 ? { scale: 0.98 } : {}}
                className={`px-6 py-3 text-sm font-semibold rounded-lg transition-all ${
                  currentStep === 0
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Back
              </motion.button>

              <motion.button
                onClick={handleNext}
                disabled={
                  isSubmitting ||
                  (currentStep === 0 &&
                    formData.hasReferralCode === true &&
                    (!formData.validatedReferralCode ||
                      referralValidationState.isValidating))
                }
                whileHover={
                  !isSubmitting &&
                  !(
                    currentStep === 0 &&
                    formData.hasReferralCode === true &&
                    (!formData.validatedReferralCode ||
                      referralValidationState.isValidating)
                  )
                    ? { scale: 1.02 }
                    : {}
                }
                whileTap={
                  !isSubmitting &&
                  !(
                    currentStep === 0 &&
                    formData.hasReferralCode === true &&
                    (!formData.validatedReferralCode ||
                      referralValidationState.isValidating)
                  )
                    ? { scale: 0.98 }
                    : {}
                }
                className="px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex gap-2 items-center">
                    <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    <span>Submitting...</span>
                  </div>
                ) : currentStep === TOTAL_STEPS - 1 ? (
                  "Submit"
                ) : (
                  "Continue"
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
